import { useNavigate } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { PAISES_META } from '../types/consumoMeta'
import type { ConsumoMeta } from '../types/consumoMeta'

const fmt = (n: number) =>
  new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' }).format(n)

interface Props {
  registros: ConsumoMeta[]
  isLoading: boolean
  anio: number
  mes: number
}

export function ConsumoMetaTable({ registros, isLoading, anio, mes }: Props) {
  const navigate = useNavigate()

  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    )
  }

  if (!registros.length) {
    return (
      <div className="py-12 text-center text-gray-500 dark:text-gray-400">
        No hay registros para los filtros seleccionados.
      </div>
    )
  }

  // Agrupar por laboratorio
  const grupos = Object.values(
    registros.reduce<Record<number, { labId: number; labNombre: string; items: ConsumoMeta[] }>>(
      (acc, r) => {
        if (!acc[r.laboratorio_id]) {
          acc[r.laboratorio_id] = { labId: r.laboratorio_id, labNombre: r.laboratorio_nombre, items: [] }
        }
        acc[r.laboratorio_id].items.push(r)
        return acc
      },
      {}
    )
  )

  return (
    <Table>
      <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] [&_tr]:hover:bg-gray-100 dark:[&_tr]:hover:bg-[hsl(220,15%,16%)]">
        <TableRow>
          <TableHead>Laboratorio</TableHead>
          <TableHead>País</TableHead>
          <TableHead className="text-right">Total envíos</TableHead>
          <TableHead className="text-right">Total $</TableHead>
          <TableHead className="text-right">Utilidad</TableHead>
          <TableHead className="text-right">Marketing</TableHead>
          <TableHead className="w-28" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {grupos.map(({ labId, labNombre, items }) => {
          const totalEnvios = items.reduce((s, r) => s + r.total_envio, 0)
          const totalDolares = items.reduce((s, r) => s + r.total_valor, 0)
          const totalUtilidad = items.filter((r) => r.tipo_envio === 'utilidad').reduce((s, r) => s + r.total_envio, 0)
          const totalMarketing = items.filter((r) => r.tipo_envio === 'marketing').reduce((s, r) => s + r.total_envio, 0)
          const paises = [...new Set(items.map((r) => r.pais))]
            .map((v) => PAISES_META.find((p) => p.value === v)?.label ?? v)
            .join(', ')

          return (
            <TableRow key={labId}>
              <TableCell className="font-semibold">{labNombre}</TableCell>
              <TableCell className="text-sm text-gray-500 dark:text-gray-400">{paises}</TableCell>
              <TableCell className="text-right tabular-nums font-semibold">
                {totalEnvios.toLocaleString('es-EC')}
              </TableCell>
              <TableCell className="text-right tabular-nums font-semibold text-emerald-700 dark:text-emerald-400">
                {fmt(totalDolares)}
              </TableCell>
              <TableCell className="text-right tabular-nums text-blue-700 dark:text-blue-400">
                {totalUtilidad.toLocaleString('es-EC')}
              </TableCell>
              <TableCell className="text-right tabular-nums text-emerald-700 dark:text-emerald-400">
                {totalMarketing.toLocaleString('es-EC')}
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 text-xs text-emerald-700 dark:text-emerald-400 hover:text-emerald-800 hover:bg-emerald-500/10"
                  onClick={() => navigate(`/consumo-meta/lab/${labId}?anio=${anio}&mes=${mes}`)}
                >
                  Detalles
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
